import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import CurrencyInput from 'components/shared/CurrencyInput/CurrencyInput';
import { useComment } from 'components/shared/ExpandableToolbar/components/ActivityLog/useComment';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Switch from 'components/shared/Switch/Switch';
import Textarea from 'components/shared/Textarea/Textarea';
import { calculateRefundBreakdown } from 'components/shared/Transactions/components/lib/calculateRefundBreakdown';

const RefundAdminModal = ({ isOpen, onClose, transaction }) => {
  const navigate = useNavigate();
  const { id, amount_paid_self, surcharge_amount, amount_refunded } = transaction || {};

  const { maxAmount } = useMemo(
    () =>
      calculateRefundBreakdown({
        amount_paid_self,
        surcharge_amount,
        amount_refunded,
        refundAmount: 0
      }),
    [amount_paid_self, surcharge_amount, amount_refunded]
  );

  const formik = useFormik({
    initialValues: {
      refundAmount: maxAmount,
      noteValue: '',
      includeSurcharge: true
    },
    validationSchema: Yup.object().shape({
      refundAmount: Yup.number().required('Refund amount is required')
    })
  });

  const { onCommentPostHandler } = useComment('transaction', id);

  const { values, errors, setFieldValue } = formik;

  const { mutateAsync: refundTransaction, isLoading } = useCustomMutation({
    mutationFn: (params) => refundTransaction(navigate, params),
    successMessage: 'Successfully refunded transaction!',
    errorMessage: 'Failed to refund transaction!',
    invalidateQueryKey: ['transactions', 'activities', 'transaction', 'patientBalance']
  });

  const refundTransactionHandler = async () => {
    await refundTransaction({
      transactionId: id,
      amount: Number(values.refundAmount),
      includeSurcharge: !!values.includeSurcharge
    });
    if (values.noteValue) {
      await onCommentPostHandler(values.noteValue);
    }
  };

  return (
    <Confirm
      icon="info-filled"
      iconSize={42}
      variant="warning"
      title="Refund Transaction"
      message="Are you sure you want to refund this Transaction?"
      handleOpen={isOpen}
      handleClose={onClose}
      footerActions={{
        primary: {
          text: 'Refund',
          onClick: refundTransactionHandler,
          color: 'warning',
          isLoading: isLoading,
          isDisabled: isLoading
        }
      }}>
      <div className="mt-4 flex flex-col gap-4">
        <CurrencyInput
          id="refund_amount"
          label="Refund Amount"
          value={values.refundAmount}
          onValueChange={(value) => setFieldValue('refundAmount', value)}
          labelClassName="text-sm font-medium"
          maxValue={maxAmount}
          required
          minValue={1}
        />

        <Textarea
          label="Refund note"
          placeholder="Add any note here"
          value={values.noteValue}
          onChange={(e) => setFieldValue('noteValue', e.target.value)}
          error={errors.noteValue && errors.noteValue.message}
          errorClassName="!mt-1"
        />

        {surcharge_amount ? (
          <div className="w-100 mt-[12px] flex items-center justify-between">
            <Switch
              label="Refund Technology Fee?"
              onChange={(checked) => setFieldValue('includeSurcharge', checked)}
              checked={values.includeSurcharge}
            />
          </div>
        ) : null}
      </div>
    </Confirm>
  );
};

export default RefundAdminModal;
